<script setup lang="ts">
import { CULTURE } from '@/composables/helpers'
import type { IAccoItem, IAccosApiResponse } from '@/composables/interfaces/apiInterfaces'
import { useSearchApi } from '@/composables/useApi'
import { biggifyDialog, width, windowWidth } from '@/composables/windowSize'
import { mediaBreakpointsAsNumber } from '@/constants'
import t from '@/i18n/translations'
import { accosApiResults, filtersData, filterStore, ratingsFilterSelected, ratingsFilterSelectedIgnoreUpdates } from '@/store/filter.store'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import EnquiryBar from '../enquirybar/EnquiryBar.vue'
import AccosMap from './AccosMap.vue'
import AppliedFilters from './FilterPageAppliedFilters.vue'
import FilterButton from './FilterPageButton.vue'
import FilterResults from './FilterPageResults.vue'
import SideBarFilter from './FilterPageSideBarFilter.vue'

const showFilters = ref(false)
const showPlaceholder = ref(false)
const loadSideBarFilterComponent = computed(() => width.value < mediaBreakpointsAsNumber.sm)
const mapResults = ref<IAccoItem[] | null>(null)
const showMap = ref(false)
const searching = ref(false)
let throttleTimer: boolean

function removeInfiniteScroll(): void {
  window.removeEventListener('scroll', handleInfiniteScroll)
}

async function handleInfiniteScroll(): Promise<void> {
  throttle(async () => {
    if (searching.value) return

    const endOfPage = window.innerHeight + window.scrollY >= document.body.offsetHeight - 500

    if (
      endOfPage &&
      accosApiResults.value != null &&
      accosApiResults.value.accos != null &&
      accosApiResults.value.accos.length < accosApiResults.value.countAll
    )
      await onSearch(true)
  }, 100)
}

async function handleSearch(next: boolean = false): Promise<void> {
  if (showMap.value) {
    await onMapSearch(next)
  } else {
    await onSearch(next)
  }
}

async function onSearch(next: boolean = false): Promise<void> {
  if (!next) {
    removeInfiniteScroll()
    window.scrollTo({ top: 0, behavior: 'smooth' })
    showPlaceholder.value = true
  }
  searching.value = true

  const useSearchApiResult = await useSearchApi(
    filterStore.value,
    next ? accosApiResults.value?.next?.from : undefined,
    next ? accosApiResults.value?.next?.take : undefined,
    filtersData.value!.seed?.accosSeed ?? 0
  )
  const data = useSearchApiResult.data
  if (data != null) {
    checkRemoveInfiniteScroll(data, next)

    accosApiResults.value = next
      ? {
          ...data,
          accos: [...accosApiResults.value!.accos, ...data.accos]
        }
      : data
  }
  setTimeout(() => {
    showPlaceholder.value = false
  }, 200)
  searching.value = false
}

async function onMapSearch(next: boolean = false): Promise<void> {
  const data = await (await useSearchApi(filterStore.value, 0, 9999999, 0)).data
  if (data != null) {
    accosApiResults.value = next
      ? {
          ...data,
          accos: [...accosApiResults.value!.accos, ...data.accos]
        }
      : data

    mapResults.value = data.accos
  }
}

function checkRemoveInfiniteScroll(results: IAccosApiResponse, next: boolean): void {
  if ((results && !results.next) || (results.accos && results.accos.length >= (results.next?.from ?? 0) + (results.next?.take ?? 0))) {
    removeInfiniteScroll()
  } else {
    if (!next) {
      window.addEventListener('scroll', handleInfiniteScroll)
    }
  }
}

function toogleShowFilters(): void {
  showFilters.value = !showFilters.value
}

watch(windowWidth, (newWidth) => {
  if (newWidth >= mediaBreakpointsAsNumber.sm) {
    showFilters.value = false
  }
})

function throttle(callback: Function, time: number): void {
  if (throttleTimer) return

  throttleTimer = true

  setTimeout(() => {
    callback()
    throttleTimer = false
  }, time)
}

function onOpenMap(open: boolean): void {
  showMap.value = open
  showFilters.value = false
}

onMounted(async () => {
  removeInfiniteScroll()

  await onSearch()
  if (filterStore.value.ratingFrom && filterStore.value.ratingTo) {
    ratingsFilterSelectedIgnoreUpdates(() => {
      ratingsFilterSelected.value = Array.from(
        { length: filterStore.value.ratingTo - filterStore.value.ratingFrom + 1 },
        (v, i) => filterStore.value.ratingFrom + i
      )
    })
  }
})

onUnmounted(() => {
  removeInfiniteScroll()
})

watch(showMap, (show) => {
  if (!show) {
    onSearch()
  }
})

watch(filterStore, () => handleSearch(), { immediate: false, deep: true })
</script>

<template>
  <div class="flex gap-8 text-[var(--p-text-color)]">
    <div v-if="!loadSideBarFilterComponent" class="hidden w-4/12 self-start sm:block">
      <SideBarFilter :accos="accosApiResults?.accos || []" :hideMap="false" @showMap="onOpenMap"></SideBarFilter>
    </div>
    <div class="w-full max-w-screen-xl">
      <div class="mb-6 flex flex-col items-center justify-between gap-2 sm:flex-row md:mb-2" v-if="accosApiResults">
        <h1 class="flex w-full flex-col gap-1 text-center font-bold md:text-left md:text-xl">
          <p v-if="accosApiResults.areaName">{{ accosApiResults.areaName }}</p>
          <p
            :class="[
              {
                'text-sm font-bold md:text-xl': !accosApiResults.areaName,
                'mb-1 text-sm font-normal': accosApiResults.areaName
              }
            ]"
          >
            {{ t(`${CULTURE}.filter.resultFound`) }}: {{ accosApiResults.countAll || 0 }}
          </p>
        </h1>
        <div class="flex items-center gap-2 sm:hidden">
          <Button id="mobilemap" v-if="false" size="small" class="pui-filterButton" @click="onOpenMap(true)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="my-auto mr-1 size-4">
              <path
                fill-rule="evenodd"
                d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                clip-rule="evenodd"
              />
            </svg>

            <span> {{ t(`${CULTURE}.filter.map.show`) }}</span>
          </Button>
          <FilterButton :hidden-media="'sm'" @showFilters="toogleShowFilters" />
        </div>
      </div>
      <AppliedFilters v-if="accosApiResults"></AppliedFilters>
      <FilterResults v-if="accosApiResults" :results="accosApiResults.accos" :show-placeholder="showPlaceholder"></FilterResults>
    </div>
  </div>
  <Dialog
    v-model:visible="showFilters"
    class="h-full max-h-full w-full rounded-none bg-white md:w-auto"
    :contentClass="'ui-filter-modal mb-24'"
    :baseZIndex="9999999"
    pt:header:class="flex-wrap flex py-2 px-4 shadow-md"
    pt:headeractions:class="w-[10%] justify-end"
    modal
  >
    <template #header>
      <div class="flex w-[90%] items-center justify-between">
        <span class="text-xl font-bold">{{ t(`${CULTURE}.filter.header`) }}</span>
      </div>
      <AppliedFilters :filtersData="filtersData" class="order-2" v-if="accosApiResults"></AppliedFilters>
    </template>

    <div class="mt-4 flex flex-col justify-center">
      <SideBarFilter :filtersData="filtersData" :hideMap="true" :accos="accosApiResults?.accos || []" @showMap="onOpenMap"></SideBarFilter>
      <div class="!fixed bottom-4 left-4 right-4 m-auto bg-white">
        <Button
          :class="{ 'opacity-60': accosApiResults?.countAll === 0 }"
          class="mt-2 flex w-full flex-col gap-0 border-primary bg-primary"
          @click="accosApiResults?.countAll != 0 && toogleShowFilters()"
        >
          <div class="font-bold">
            {{ accosApiResults?.countAll !== 0 ? t(`${CULTURE}.filter.actionButtonLabel`) : t(`${CULTURE}.filter.noResultActionButtonLabel`) }}
          </div>
          <div class="text-sm" v-if="accosApiResults">{{ t(`${CULTURE}.filter.resultFound`) }}: {{ accosApiResults.countAll || 0 }}</div>
        </Button>
      </div>
    </div>
  </Dialog>
  <Dialog
    class="h-full max-h-full w-full bg-white"
    v-model:visible="showMap"
    :pt="biggifyDialog()"
    :baseZIndex="9999999"
    :contentClass="'h-full !p-0'"
    :showHeader="false"
    modal
  >
    <AccosMap
      :accos="mapResults || []"
      @close="
        () => {
          showMap = false
        }
      "
      @mapSearch="onMapSearch"
      @showFilters="toogleShowFilters"
    />
  </Dialog>
  <EnquiryBar :usedOnFilterPage="true" />
</template>
